import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme
} from "@mui/material";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { urls } from "@routes";
import { format } from "date-fns";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { LocalFireDepartment } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { exportToCSV, CSVColumn, GenericCsvExportButton, colDefsToCsvColumns } from "@components/ExportToCSV";
import {useAuth} from "./AuthProvider";

interface BurntDomain {
	id: number;
	domain: string;
	user_email: string;
	user_id: number;
	workspace_name: string;
	workspace_id: number;
	status: string;
	created_on_ts: number;
	burnt: boolean;
	burnt_detected_on: number | null;
	burnt_via: string;
	burnt_at: string;
	warmup_running: boolean;
	running_campaigns: string[];
}

interface Summary {
	total_burnt: number;
	burnt_at_warmup: number;
	burnt_at_campaign: number;
	burnt_last_7_days: number;
	warmup_still_running: number;
}

interface PageData {
	data: BurntDomain[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
	summary: Summary;
}

export default function AdminBurntDomains() {
	const theme = useTheme();
	const { enqueueSnackbar } = useSnackbar();

	const [pageData, setPageData] = useState<PageData>();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [searchQuery, setSearchQuery] = useState("");
	const [burntAtFilter, setBurntAtFilter] = useState("");

	const burntDomainsQuery = useQuery({
		queryKey: ["adminBurntDomainsQuery", currentPage, pageSize, searchQuery, burntAtFilter],
		queryFn: () => {
			let url = `/admin/burnt-domains/?page=${currentPage}&page_size=${pageSize}`;
			if (searchQuery.trim()) {
				url += `&search=${encodeURIComponent(searchQuery)}`;
			}
			if (burntAtFilter) {
				url += `&burnt_at=${burntAtFilter}`;
			}
			return authenticateAndFetchData(url);
		},
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (burntDomainsQuery.data) {
			setPageData(burntDomainsQuery.data.data);
		}
	}, [burntDomainsQuery.data]);

	const unmarkBurntMutation = useMutation({
		mutationKey: ["unmarkDomainBurntFromList"],
		mutationFn: (domainId: number) =>
			authenticateAndPostData("/admin/domain/unmark-burnt/", { id: domainId }),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			burntDomainsQuery.refetch().then();
			enqueueSnackbar("Domain unmarked as burnt. Warmup will resume, campaigns stay paused.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data?.message || "Failed to unmark domain as burnt", {
				variant: "error",
			});
		},
	});

	function timestampFormatter(params: ValueFormatterParams) {
		if (params.value === null || params.value === undefined) {
			return "---";
		}
		return format(new Date(params.value), "do MMM y, h:mm a");
	}

	const DomainLink = (cellProps: CustomCellRendererProps) => (
		<Link
			to={urls["adminUserConnectedDomains"].replace(":userId", cellProps.data["user_id"])}
			style={{ color: theme.palette.text.primary }}
		>
			{cellProps.value}
		</Link>
	);

	const UserEmailLink = (cellProps: CustomCellRendererProps) => (
		<Link
			to={urls["adminUserDetails"].replace(":userId", cellProps.data["user_id"])}
			style={{ color: theme.palette.text.primary }}
		>
			{cellProps.value}
		</Link>
	);

	const BurntAtRenderer = (cellProps: CustomCellRendererProps) => {
		if (!cellProps.value) return <span style={{ color: "#999" }}>---</span>;
		const isWarmup = String(cellProps.value).toLowerCase() === "warmup";
		return (
			<Chip
				label={isWarmup ? "Warmup" : "Campaign"}
				size="small"
				color={isWarmup ? "warning" : "info"}
			/>
		);
	};

	const WarmupRenderer = (cellProps: CustomCellRendererProps) => (
		cellProps.value ? (
			<Tooltip title="Warmup is still running on a burnt domain. Check the burnt-domain feature flags, or pause it manually.">
				<Chip label="Still running" color="error" size="small" />
			</Tooltip>
		) : (
			<Chip label="Paused" color="default" size="small" variant="outlined" />
		)
	);

	const RunningCampaignsRenderer = (cellProps: CustomCellRendererProps) => {
		const campaigns: string[] = cellProps.value || [];
		if (campaigns.length === 0) {
			return <span style={{ color: "#999" }}>None</span>;
		}
		return (
			<Tooltip title={campaigns.join(", ")}>
				<Chip label={`${campaigns.length} running`} color="error" size="small" />
			</Tooltip>
		);
	};

	const ActionsRenderer = (cellProps: CustomCellRendererProps) => (
		<Button
			size="small"
			variant="outlined"
			color="warning"
			disabled={unmarkBurntMutation.isPending}
			onClick={() => {
				if (window.confirm(
					`Unmark ${cellProps.data["domain"]} as burnt? Warmup resumes, paused campaigns stay paused.`
				)) {
					unmarkBurntMutation.mutate(cellProps.data["id"]);
				}
			}}
		>
			Unmark
		</Button>
	);

	const columnCustomizerLabel = {
		domain: "Domain",
		user_email: "User Email",
		workspace_name: "Workspace",
		burnt_detected_on: "Burnt On",
		burnt_via: "Burnt Via",
		burnt_at: "Burnt At",
		warmup_running: "Warmup",
		running_campaigns: "Running Campaigns",
		status: "Domain Status",
		created_on_ts: "Domain Created",
	};

	const columnDefs: ColDef[] = [
		{ field: "domain", headerName: "Domain", cellRenderer: DomainLink, minWidth: 220 },
		{ field: "user_email", headerName: "User Email", cellRenderer: UserEmailLink, minWidth: 220 },
		{ field: "workspace_name", headerName: "Workspace", minWidth: 160 },
		{
			field: "burnt_detected_on",
			headerName: "Burnt On",
			valueFormatter: timestampFormatter,
			minWidth: 190,
			sort: "desc",
		},
		{ field: "burnt_via", headerName: "Burnt Via", minWidth: 220 },
		{ field: "burnt_at", headerName: "Burnt At", cellRenderer: BurntAtRenderer, minWidth: 130 },
		{ field: "warmup_running", headerName: "Warmup", cellRenderer: WarmupRenderer, minWidth: 140 },
		{
			field: "running_campaigns",
			headerName: "Running Campaigns",
			cellRenderer: RunningCampaignsRenderer,
			minWidth: 170,
			sortable: false,
		},
		{ field: "status", headerName: "Domain Status", minWidth: 140 },
		{
			field: "created_on_ts",
			headerName: "Domain Created",
			valueFormatter: timestampFormatter,
			minWidth: 190,
		},
		{
			headerName: "Actions",
			colId: "actions",
			cellRenderer: ActionsRenderer,
			sortable: false,
			resizable: false,
			minWidth: 120,
		},
	];

	function handlePageChange(page: number) {
		setCurrentPage(page);
	}

	function handlePageSizeChange(size: number) {
		setPageSize(size);
		setCurrentPage(1);
	}

	function handleSearchChange(search: string) {
		setSearchQuery(search);
		setCurrentPage(1);
	}

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (burntDomainsQuery.isLoading && !pageData) {
		return <PageLoading />;
	}

	if (burntDomainsQuery.isError) {
		return <PageDataErrorHandler error={burntDomainsQuery.error as unknown as ApiRequestFailed} />;
	}

	const summary = pageData?.summary;

	return (
		<Stack
			direction="column"
			spacing={3}
			sx={{
				pb: 4,
				height: "100%",
				width: "100%",
				maxWidth: "100vw",
				overflowX: "hidden"
			}}
		>
			<Box display="flex" alignItems="center" gap={1}>
				<LocalFireDepartment color="error" />
				<Typography variant="h4">Burnt Domains</Typography>
			</Box>

			<Typography color="text.secondary">
				Domains a receiving provider rejected on sending-domain reputation. Each row records the
				bounce that revealed it - the recipient address and whether it surfaced during warmup or a
				campaign.
			</Typography>

			{summary && summary.warmup_still_running > 0 && (
				<Alert severity="warning">
					{summary.warmup_still_running} burnt {summary.warmup_still_running === 1 ? "domain has" : "domains have"}
					{" "}warmup still running. Either the pause-warmup flag was off when they were flagged, or
					the warmup API call failed - they are still sending.
				</Alert>
			)}

			{summary && (
				<Box display="flex" gap={2} flexWrap="wrap">
					<Card sx={{ minWidth: 140 }}>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Stack spacing={0.5}>
								<Typography variant="caption" color="text.secondary" fontWeight="bold">Total Burnt</Typography>
								<Typography variant="h5" fontWeight="bold">{summary.total_burnt}</Typography>
							</Stack>
						</CardContent>
					</Card>
					<Card sx={{ minWidth: 140 }}>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Stack spacing={0.5}>
								<Typography variant="caption" color="text.secondary" fontWeight="bold">Last 7 Days</Typography>
								<Typography variant="h5" fontWeight="bold">{summary.burnt_last_7_days}</Typography>
							</Stack>
						</CardContent>
					</Card>
					<Card sx={{ minWidth: 140 }}>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Stack spacing={0.5}>
								<Typography variant="caption" color="text.secondary" fontWeight="bold">Via Warmup</Typography>
								<Typography variant="h5" fontWeight="bold">{summary.burnt_at_warmup}</Typography>
							</Stack>
						</CardContent>
					</Card>
					<Card sx={{ minWidth: 140 }}>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Stack spacing={0.5}>
								<Typography variant="caption" color="text.secondary" fontWeight="bold">Via Campaign</Typography>
								<Typography variant="h5" fontWeight="bold">{summary.burnt_at_campaign}</Typography>
							</Stack>
						</CardContent>
					</Card>
					<Card sx={{ minWidth: 160 }}>
						<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
							<Stack spacing={0.5}>
								<Typography variant="caption" color="text.secondary" fontWeight="bold">Warmup Still Running</Typography>
								<Typography
									variant="h5"
									fontWeight="bold"
									color={summary.warmup_still_running > 0 ? "error" : "inherit"}
								>
									{summary.warmup_still_running}
								</Typography>
							</Stack>
						</CardContent>
					</Card>
				</Box>
			)}

			<Divider />

			<Box sx={{ width: "100%", minHeight: { xs: 400, md: 500 }, display: "flex", flexDirection: "column" }}>
				<PaginatedServerSideDataTable
					columns={columnDefs}
					paginationData={pageData}
					loading={burntDomainsQuery.isLoading || burntDomainsQuery.isFetching}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					onSearchChange={handleSearchChange}
					searchPlaceholder="Search by domain, user email, workspace, or burnt-via address..."
					noRowsText="No burnt domains. Nothing has been flagged."
					columnLabels={columnCustomizerLabel}
					actions={(
						<>
						<TextField
							select
							size="small"
							label="Burnt At"
							value={burntAtFilter}
							onChange={(e) => {
								setBurntAtFilter(e.target.value);
								setCurrentPage(1);
							}}
							sx={{ minWidth: 160, mr: 2 }}
						>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="warmup">Warmup</MenuItem>
							<MenuItem value="campaign">Campaign</MenuItem>
						</TextField>
						<GenericCsvExportButton
							endpoint="/admin/burnt-domains/"
							queryParams={{
								search: searchQuery,
								burnt_at: burntAtFilter,
							}}
							columns={colDefsToCsvColumns(columnDefs)}
							exportName={"BurntDomains"}
							transformResponse={(res) => res?.data?.data || []}
						/>
						</>
					)}
				/>
			</Box>
		</Stack>
	);
}
